/* ==========================================================================
   B&B – World (bb-world.js)
   Videos play silently on a loop only while on screen, and each has a
   pause/play button. With reduced motion turned on, videos start paused.
   ========================================================================== */

(() => {
  if (customElements.get('bb-world')) return;

  class BBWorld extends HTMLElement {
    connectedCallback() {
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.items = Array.from(this.querySelectorAll('.bb-wd__tile')).map((tile) => ({
        video: tile.querySelector('video'),
        toggle: tile.querySelector('[data-bb-toggle]'),
        sound: tile.querySelector('[data-bb-sound]'),
        userPaused: this.reducedMotion,
        visible: false
      })).filter((item) => item.video);

      if (!this.items.length) return;

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const item = this.items.find((i) => i.video === entry.target);
          if (!item) return;
          item.visible = entry.isIntersecting;
          this.update(item);
        });
      }, { threshold: 0.35 });

      this.items.forEach((item) => {
        item.video.muted = true;
        item.video.loop = true;
        item.video.playsInline = true;
        item.video.removeAttribute('controls');

        item.toggle?.addEventListener('click', () => {
          item.userPaused = !item.userPaused;
          this.update(item);
        });

        // Testimonials: sound on/off. Turning one on mutes the others and
        // makes sure that video is playing.
        item.sound?.addEventListener('click', () => {
          const turnOn = item.video.muted;
          this.items.forEach((other) => {
            if (other !== item) this.setSound(other, false);
          });
          this.setSound(item, turnOn);
          if (turnOn && item.userPaused) {
            item.userPaused = false;
            this.update(item);
          }
        });

        this.update(item);
        this.observer.observe(item.video);
      });
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.items?.forEach((item) => item.video.pause());
    }

    setSound(item, on) {
      item.video.muted = !on;
      if (item.sound) {
        item.sound.setAttribute('aria-pressed', String(on));
        item.sound.setAttribute('aria-label', on ? 'Turn sound off' : 'Turn sound on');
      }
    }

    update(item) {
      const shouldPlay = item.visible && !item.userPaused;

      if (shouldPlay) {
        const attempt = item.video.play();
        if (attempt && attempt.catch) attempt.catch(() => {});
      } else {
        item.video.pause();
        if (!item.visible) this.setSound(item, false);
      }

      if (item.toggle) {
        item.toggle.setAttribute('aria-pressed', String(item.userPaused));
        item.toggle.setAttribute('aria-label', item.userPaused ? 'Play video' : 'Pause video');
      }
    }
  }

  customElements.define('bb-world', BBWorld);
})();